import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/lib/Async';
import { omit } from 'underscore';
import { Button, InputGroup } from 'reactstrap';
import FontAwesome from 'react-fontawesome';

export const css = {
  container: (base, state) => ({
    ...base,
    // minWidth: 180,
    alignItems: 'stretch !important',
    maxHeight: 34
  }),
  control: (base, state) => ({
    ...base,
    width: '100%',
    // minWidth: 180,
    borderRadius: 0,
    backgroundColor: '#fff',
    borderColor: '#e7e7e7',
    boxShadow: 'inset 0 1px 1px rgba(0, 0, 0, 0.075)',
    minHeight: '34px',
    maxHeight: '34px',
    color: '#495057',
    flexWrap: 'nowrap',
    ...(state.isFocused
      ? {
          borderColor: '#80bdff',
          outline: 0,
          boxShadow:
            'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 0.2rem rgba(0, 123, 255, 0.25)'
        }
      : {})
  }),
  valueContainer: (base, state) => ({
    ...base,
    '& > div > div': {
      display: 'block !important'
    },
    '& input': {
      width: '100% !important',
      opacity: '1 !important',
      color: '#495057 !important'
    }
  }),
  indicatorsContainer: (base, state) => ({
    ...base,
    display: 'none'
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: '6px 8px'
  }),
  menuPortal: (base, state) => ({
    ...base,
    width: 'auto',
    minWidth: '300px',
    zIndex: 5
  }),
  input: (base, state) => ({
    ...base,
    width: '100%'
  }),
  placeholder: (base, state) => ({
    ...base,
    left: 0,
    margin: '0px',
    padding: '0px 8px',
    width: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  })
};

class TypeAhead extends PureComponent {
  static contextTypes = {
    theme: PropTypes.object
  };

  static defaultProps = {
    isClearable: true,
    showSubmit: false
  };

  constructor(props) {
    super(props);
    this.state = {
      value: this.getValue() || '',
      // track this ourselves because react-select likes to autoselect the first value
      menuFocused: false,
      hasSearched: false
    };
    this.lastRequest = undefined;
    this.lastRequestTimeout = undefined;
    this.mounted = false;
    this.lastOptions = undefined;
  }

  render() {
    const {
      defaultOptions,
      input,
      loadOptions,
      meta,
      labelAsValue,
      styles,
      isClearable,
      showSubmit,
      groupClassName,
      submitClassName,
      menuIsOpen,
      onChange,
      onBlur,
      injectFirstValue = true,
      ...props
    } = this.props;
    const { hasSearched, value } = this.state;
    const BS = this.context.theme;

    return (
      <InputGroup className={groupClassName}>
        <AsyncSelect
          ref={select => (this.select = select)}
          className={BS.customFile}
          cacheOptions
          defaultOptions={defaultOptions || false}
          {...props}
          {...omit(input, 'onChange', 'value')}
          onBlur={onBlur || input.onBlur}
          value={value || undefined}
          inputValue={value && value.value ? value.value : value}
          menuIsOpen={
            hasSearched && !!(this.lastRequest || !value) ? undefined : !!menuIsOpen
          }
          isOptionSelected={(option, options) => {
            return false;
          }}
          loadOptions={(inputValue, callback) => {
            if (this.lastRequestTimeout) {
              clearTimeout(this.lastRequestTimeout);
            }

            if (this.lastRequest && this.lastRequest.abort) {
              this.lastRequest.abort();
              this.lastRequest = undefined;
            }

            this.lastRequestTimeout = setTimeout(() => {
              if (!this.mounted) {
                return;
              }
              // this supports multiple callback for the same load
              var lastRequest = this.select ? this.select.lastRequest : undefined;
              this.lastRequest = loadOptions(inputValue, options => {
                const select = this.select;
                if (!select || !this.mounted) {
                  return;
                }

                if (injectFirstValue) {
                  options = [
                    {
                      label: 'Search All',
                      field: 'all',
                      useInput: true
                    }
                  ].concat(options);
                }

                this.lastOptions = options;
                if (lastRequest && select.lastRequest === undefined) {
                  select.lastRequest = lastRequest;
                  lastRequest = undefined;
                }

                callback(options);
              });

              if (this.lastRequest.then) {
                this.lastRequest.then(options => (this.lastOptions = options));
              }

              if (!hasSearched) {
                this.setState({
                  hasSearched: true
                });
              }

              return {
                abort: () => {
                  if (this.lastRequest && this.lastRequest.abort) {
                    this.lastRequest.abort();
                  }

                  if (this.lastRequestTimeout) {
                    clearTimeout(this.lastRequestTimeout);
                  }
                }
              };
            }, 500);
          }}
          styles={{
            ...css,
            ...(styles || {})
          }}
          menuPortalTarget={document.getElementById('body')}
          controlShouldRenderValue={false}
          onInputChange={(value, { action }) => {
            switch (action) {
              case 'input-change':
                this.setState({ value });
                break;
              case 'input-blur':
              case 'menu-close':
                return this.state.value;
              default:
                break;
            }
          }}
          onChange={(value, { action }) => {
            var newValue = value;

            if (onChange) {
              // should return the same value if you want default handling
              newValue = onChange(value, { action });
            }

            if (newValue !== undefined && action === 'select-option') {
              if (newValue !== false) {
                if (newValue.useInput) {
                  newValue = this.state.value;
                }
                newValue = labelAsValue ? newValue.label : newValue;
                input.onChange(newValue);
              }

              this.setState({
                value: newValue,
                menuFocused: false
              });
            }
          }}
          onKeyDown={e => {
            const select = this.select;
            if (e.keyCode === 13 && !e.shiftKey) {
              // Resets the focused state for tracking what ENTER does
              this.setState({
                menuFocused: false
              });

              // When hitting ENTER we need to try and detect if what the user wants
              // is being typed or being selected
              if (!this.state.menuFocused && select.select.state.menuIsOpen) {
                input.onChange(this.state.value);
                e.preventDefault();
              }
            } else if (!this.state.menuFocused) {
              // First time we should just display option and not change selected
              // if (
              //   !this.state.menuFocused &&
              //   (e.key === 'ArrowUp' || e.key === 'ArrowDown')
              // ) {
              //   e.preventDefault();
              // }

              switch (e.key) {
                case 'ArrowUp':
                case 'ArrowDown':
                case 'PageUp':
                case 'PageDown':
                  this.setState({
                    menuFocused: true
                  });
                  break;
                case 'Home':
                case 'End':
                  e.defaultPrevented = true;
                  break;
                default:
                  break;
              }
            }
          }}
        />
        {isClearable && !!value && (
          <Button
            color="white"
            style={{ borderLeft: 0 }}
            onClick={() => input.onChange('')}
            title="Clear Value"
          >
            <FontAwesome name="times-circle" fixedWidth />
          </Button>
        )}
        {showSubmit && (
          <Button color="dark-blue" type="submit" className={submitClassName}>
            GO
          </Button>
        )}
      </InputGroup>
    );
  }

  componentWillReceiveProps(nextProps) {
    const oldValue = this.getValue();
    const newValue = this.getValue(nextProps);

    if (oldValue !== newValue && newValue !== this.state.value && newValue) {
      // A bit dirty but whatever
      this.select.setState({
        isLoading: true
      });
      this.select.loadOptions(newValue, options => {
        if (!this.select || !this.select.mounted) return;
        const isLoading = !!this.select.lastRequest;
        this.select.optionsCache[newValue] = options;
        this.select.setState({ defaultOptions: options || [], isLoading });
      });
    }

    if (oldValue !== newValue) {
      this.setState({
        value: newValue
      });
    }
  }

  getValue(props) {
    props = props || this.props;
    const value = props.input ? props.input.value : props.value;

    return value ? value.value || value : value;
  }

  componentDidMount() {
    this.mounted = true;
  }

  comonentWillUnmount() {
    this.mounted = false;
  }
}

export default TypeAhead;



// WEBPACK FOOTER //
// ./src/components/form/field/typeahead.js